export const translations = {
  en: {
    nav: {
      home: 'Home',
      support: 'Support',
      privacy: 'Privacy'
    },
    home: {
      hero_title: 'FutureTick Simulator',
      hero_subtitle: 'Master Chinese Futures Trading with Historical Data Playback',
      hero_description: 'Experience real market conditions through historical tick data playback. Practice your trading strategies with simulated orders in a risk-free environment.',
      download_ios: 'Download on App Store',
      learn_more: 'Learn More',
      features_title: 'Powerful Features',
      feature1_title: 'Historical Data Playback',
      feature1_desc: 'Play back real historical tick data from Chinese futures markets with precise timestamp control.',
      feature2_title: 'Multiple Chart Periods',
      feature2_desc: 'View data in tick, 1-minute, 3-minute, and 5-minute chart formats with MA and BOLL indicators.',
      feature3_title: 'Simulated Trading',
      feature3_desc: 'Practice with virtual orders including open/close positions, buy/sell operations, and real-time P&L tracking.',
      feature4_title: 'Remote Data Access',
      feature4_desc: 'Download historical data for any contract from our cloud service directly within the app.',
      feature5_title: 'Playback Controls',
      feature5_desc: 'Full control with play/pause, progress slider, and seek functionality for detailed analysis.',
      feature6_title: 'Real-time Analytics',
      feature6_desc: 'Monitor equity, floating P&L, positions, and pending orders in real-time during playback.',
      how_title: 'How It Works',
      step1_title: 'Select Contract',
      step1_desc: 'Choose from available Chinese futures contracts or import your own .bin data files.',
      step2_title: 'Playback & Analyze',
      step2_desc: 'Watch historical tick data play back with K-line charts and technical indicators.',
      step3_title: 'Simulate Trading',
      step3_desc: 'Place orders, manage positions, and track your performance as if trading in real market conditions.',
      cta_title: 'Ready to Master Futures Trading?',
      cta_subtitle: 'Download FutureTick Simulator today and start practicing with real historical data.',
      download_now: 'Download Now'
    },
    support: {
      title: 'Support',
      intro: 'Need help with FutureTick Simulator? We\'re here to assist you.',
      contact_title: 'Contact Us',
      contact_desc: 'If you have any questions, feedback, or need technical support, please feel free to reach out to us:',
      email_label: 'Email:',
      faq_title: 'Frequently Asked Questions',
      faq1_q: 'What data format does the app support?',
      faq1_a: 'The app supports .bin format files containing packed tick data (44 bytes per record). You can also download data directly from our cloud service.',
      faq2_q: 'Is the trading simulation realistic?',
      faq2_a: 'Yes, the simulation engine processes orders based on historical tick data with realistic price matching, position management, and P&L calculation.',
      faq3_q: 'Can I import my own data files?',
      faq3_a: 'Yes, you can import .bin files from your device using the "Import .bin" button in the app.',
      faq4_q: 'What technical indicators are available?',
      faq4_a: 'The app currently supports Moving Average (MA) and Bollinger Bands (BOLL) indicators on K-line charts.',
      faq5_q: 'How do I switch between chart periods?',
      faq5_a: 'Use the period buttons at the top of the screen to switch between tick view, 1-minute, 3-minute, and 5-minute K-line charts.',
      system_title: 'System Requirements',
      system_ios: 'iOS 17.6 or later',
      system_device: 'Compatible with iPhone',
      system_storage: 'Storage space depends on data file size'
    },
    privacy: {
      title: 'Privacy Policy',
      last_updated: 'Last Updated: January 20, 2026',
      intro: 'This Privacy Policy describes how FutureTick Simulator ("we", "our", or "the app") collects, uses, and protects your information.',
      collection_title: '1. Information Collection',
      collection_content: 'FutureTick Simulator is designed with privacy in mind. The app operates primarily on your device and does not collect personal information. The app may access:',
      collection_item1: 'Data files you import (.bin files) - stored locally on your device',
      collection_item2: 'Cloud service requests when you download historical data - only contract information is transmitted',
      collection_item3: 'App usage preferences - stored locally on your device',
      usage_title: '2. How We Use Information',
      usage_content: 'Any data accessed by the app is used solely for:',
      usage_item1: 'Displaying historical tick data and charts',
      usage_item2: 'Processing simulated trading operations',
      usage_item3: 'Providing data download functionality',
      usage_item4: 'Saving your app preferences and settings',
      storage_title: '3. Data Storage',
      storage_content: 'All imported data files and trading simulation records are stored locally on your device. We do not upload or transmit your personal data or trading simulation results to any external servers except when you explicitly request to download historical data from our cloud service.',
      sharing_title: '4. Data Sharing',
      sharing_content: 'We do not sell, trade, or share your personal information with third parties. The app does not integrate with any analytics or advertising services.',
      security_title: '5. Data Security',
      security_content: 'Your data is protected by iOS security features. All files are stored within the app\'s sandbox and are not accessible to other apps.',
      children_title: '6. Children\'s Privacy',
      children_content: 'The app is not intended for children under 13. We do not knowingly collect information from children under 13.',
      changes_title: '7. Changes to Privacy Policy',
      changes_content: 'We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date at the top of this page.',
      contact_title: '8. Contact Us',
      contact_content: 'If you have any questions about this Privacy Policy, please contact us at:'
    },
    footer: {
      copyright: '© 2026 李光浩. All rights reserved.',
      home: 'Home',
      support: 'Support',
      privacy: 'Privacy Policy'
    }
  },
  zh: {
    nav: {
      home: '首页',
      support: '支持',
      privacy: '隐私政策'
    },
    home: {
      hero_title: '期货模拟器',
      hero_subtitle: '通过历史数据回放精通中国期货交易',
      hero_description: '通过历史tick数据回放体验真实市场环境。在无风险的环境中使用模拟订单练习您的交易策略。',
      download_ios: '在 App Store 下载',
      learn_more: '了解更多',
      features_title: '强大功能',
      feature1_title: '历史数据回放',
      feature1_desc: '回放中国期货市场的真实历史tick数据，精确的时间戳控制。',
      feature2_title: '多种图表周期',
      feature2_desc: '以分时、1分钟、3分钟和5分钟K线格式查看数据，支持MA和BOLL指标。',
      feature3_title: '模拟交易',
      feature3_desc: '使用虚拟订单进行练习，包括开仓/平仓、买入/卖出操作和实时盈亏跟踪。',
      feature4_title: '远程数据访问',
      feature4_desc: '直接在应用中从云服务下载任何合约的历史数据。',
      feature5_title: '回放控制',
      feature5_desc: '完全控制播放/暂停、进度条和跳转功能，便于详细分析。',
      feature6_title: '实时分析',
      feature6_desc: '在回放过程中实时监控权益、浮动盈亏、持仓和挂单。',
      how_title: '使用方法',
      step1_title: '选择合约',
      step1_desc: '从可用的中国期货合约中选择，或导入您自己的.bin数据文件。',
      step2_title: '回放与分析',
      step2_desc: '观看历史tick数据回放，配合K线图表和技术指标进行分析。',
      step3_title: '模拟交易',
      step3_desc: '下单、管理持仓，像在真实市场环境中一样跟踪您的表现。',
      cta_title: '准备好掌握期货交易了吗？',
      cta_subtitle: '立即下载期货模拟器，开始使用真实历史数据进行练习。',
      download_now: '立即下载'
    },
    support: {
      title: '支持',
      intro: '需要期货模拟器的帮助吗？我们随时为您提供协助。',
      contact_title: '联系我们',
      contact_desc: '如果您有任何问题、反馈或需要技术支持，请随时联系我们：',
      email_label: '邮箱：',
      faq_title: '常见问题',
      faq1_q: '应用支持什么数据格式？',
      faq1_a: '应用支持包含打包tick数据的.bin格式文件（每条记录44字节）。您也可以直接从我们的云服务下载数据。',
      faq2_q: '交易模拟是否真实？',
      faq2_a: '是的，模拟引擎基于历史tick数据处理订单，具有真实的价格匹配、持仓管理和盈亏计算。',
      faq3_q: '我可以导入自己的数据文件吗？',
      faq3_a: '可以，您可以使用应用中的"导入.bin"按钮从设备导入.bin文件。',
      faq4_q: '提供哪些技术指标？',
      faq4_a: '应用目前支持K线图上的移动平均线（MA）和布林带（BOLL）指标。',
      faq5_q: '如何切换图表周期？',
      faq5_a: '使用屏幕顶部的周期按钮在分时视图、1分钟、3分钟和5分钟K线图之间切换。',
      system_title: '系统要求',
      system_ios: 'iOS 17.6 或更高版本',
      system_device: '兼容 iPhone',
      system_storage: '存储空间取决于数据文件大小'
    },
    privacy: {
      title: '隐私政策',
      last_updated: '最后更新：2026年1月20日',
      intro: '本隐私政策描述了期货模拟器（"我们"、"我们的"或"应用"）如何收集、使用和保护您的信息。',
      collection_title: '1. 信息收集',
      collection_content: '期货模拟器的设计注重隐私保护。应用主要在您的设备上运行，不收集个人信息。应用可能访问：',
      collection_item1: '您导入的数据文件（.bin文件）- 本地存储在您的设备上',
      collection_item2: '当您下载历史数据时的云服务请求 - 仅传输合约信息',
      collection_item3: '应用使用偏好 - 本地存储在您的设备上',
      usage_title: '2. 信息使用方式',
      usage_content: '应用访问的任何数据仅用于：',
      usage_item1: '显示历史tick数据和图表',
      usage_item2: '处理模拟交易操作',
      usage_item3: '提供数据下载功能',
      usage_item4: '保存您的应用偏好和设置',
      storage_title: '3. 数据存储',
      storage_content: '所有导入的数据文件和交易模拟记录都本地存储在您的设备上。除非您明确请求从我们的云服务下载历史数据，否则我们不会上传或传输您的个人数据或交易模拟结果到任何外部服务器。',
      sharing_title: '4. 数据共享',
      sharing_content: '我们不会向第三方出售、交易或共享您的个人信息。应用不集成任何分析或广告服务。',
      security_title: '5. 数据安全',
      security_content: '您的数据受到iOS安全功能的保护。所有文件都存储在应用的沙盒中，其他应用无法访问。',
      children_title: '6. 儿童隐私',
      children_content: '本应用不适用于13岁以下儿童。我们不会故意收集13岁以下儿童的信息。',
      changes_title: '7. 隐私政策变更',
      changes_content: '我们可能会不时更新本隐私政策。更新版本将在本页面顶部显示更新的"最后更新"日期。',
      contact_title: '8. 联系我们',
      contact_content: '如果您对本隐私政策有任何疑问，请通过以下方式联系我们：'
    },
    footer: {
      copyright: '© 2026 李光浩. 保留所有权利。',
      home: '首页',
      support: '支持',
      privacy: '隐私政策'
    }
  }
};
